/* Temporary portrait adapter. The timer is simulated; character registration stays real. */
(() => {
  'use strict';
  const DURATION_MS = 30000;
  function createAdapter(options = {}, dependencies = {}) {
    const store = dependencies.store || globalThis.window?.WonderbookPhotoProfiles;
    const client = dependencies.client || globalThis.window?.WonderbookPhotoOnboarding?.client;
    const now = dependencies.now || Date.now;
    const urls = dependencies.URL || globalThis.URL;
    const crypto = dependencies.crypto || globalThis.crypto;
    const photo = options.originalPhoto;
    if (!store || !client?.saveProfile || !(photo instanceof Blob) || !photo.size) throw new Error('Choose a photo to create this character.');
    let job = null, profileId = null, finalInput = null, result = null, flight = null, stopped = false, sourceRevoked = false;
    const sourceUrl = urls.createObjectURL(photo);
    let photoDisplayUrl = sourceUrl;
    let display = { status: 'ready', url: sourceUrl, expiresAt: null, source: 'uploaded-photo' };
    const fail = (code, message) => Object.assign(new Error(message), { code, definitive: true });
    const guard = () => { if (stopped) throw fail('ACCOUNT_CHANGED', 'Your account changed. Reload the page to continue.'); };
    const snapshot = () => {
      guard();
      if (!job) throw fail('DRAFT_NOT_STARTED', 'Choose a photo first.');
      const ready = now() - job.startedAt >= DURATION_MS;
      const currentDisplay = /^https:\/\//i.test(display.url) && !(Date.parse(display.expiresAt) > now())
        ? { status: 'ready', url: photoDisplayUrl, expiresAt: null, source: 'uploaded-photo' } : display;
      return { draftId: job.id, status: ready ? 'ready' : 'processing', retryAfterMs: 2000,
        ...(ready ? { portrait: { ...currentDisplay } } : {}) };
    };
    // Only reuse artwork for the exact same photo. A different person in the
    // library must never become the image for a newly uploaded photo.
    async function findCachedPortrait() {
      if (!crypto?.subtle) return;
      const hash = async blob => new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())).join(',');
      const candidates = store.list().filter(profile => {
        const previous = store.getPhoto(profile.id);
        return profile.subjectType === options.personType && (profile.eyeColor || '') === (options.eyeColor || '')
          && previous?.size === photo.size && previous.type === photo.type;
      });
      if (!candidates.length) return;
      const target = await hash(photo);
      for (const candidate of candidates) {
        if (stopped || !job) return;
        if (await hash(store.getPhoto(candidate.id)) !== target) continue;
        const portrait = await client.getProfilePortrait?.(candidate.id);
        if (stopped) return;
        if (portrait?.status === 'ready' && /^https:\/\//i.test(portrait.url || '')
          && Date.parse(portrait.expiresAt) > now()) display = { ...portrait, source: 'cached-avatar' };
        return;
      }
    }
    const revokeSource = () => { if (!sourceRevoked) { sourceRevoked = true; urls.revokeObjectURL(sourceUrl); } };
    function dispose() {
      stopped = true; result = null; revokeSource();
      globalThis.window?.removeEventListener?.('wonderbook:account-changed', dispose);
    }
    globalThis.window?.addEventListener?.('wonderbook:account-changed', dispose, { once: true });
    return Object.freeze({
      dispose,
      async begin(input) {
        guard();
        if (job) {
          if (job.key !== input.requestKey) throw fail('DRAFT_INPUT_CHANGED', 'Reopen the photo step to start a new character.');
          return snapshot();
        }
        job = { id: crypto.randomUUID(), key: input.requestKey, startedAt: now() };
        void findCachedPortrait().catch(() => {});
        return snapshot();
      },
      async read(id) {
        guard();
        if (id !== job?.id) throw fail('DRAFT_NOT_FOUND', 'This photo step is no longer available.');
        return snapshot();
      },
      async recover(key) {
        guard();
        if (key === job?.key) return snapshot();
        if (key === finalInput?.requestKey) {
          if (result) return { status: 'ready', result };
          // saveProfile owns recovery and retains the exact request/photo pair.
          return { status: 'not-admitted' };
        }
        return { status: 'not-admitted' };
      },
      finalize(id, input) {
        try {
          guard();
          if (id !== job?.id || snapshot().status !== 'ready') throw fail('DRAFT_NOT_READY', 'Your character is still being prepared.');
          if (!input?.name?.trim() || !input.requestKey) throw fail('CHARACTER_NAME_REQUIRED', 'Enter the character name.');
          const fingerprint = JSON.stringify(input);
          if (finalInput && JSON.stringify(finalInput) !== fingerprint) throw fail('REQUEST_CHANGED', 'Retry saving with the same character details.');
          if (result) return Promise.resolve(result);
          if (flight) return flight;
          if (!finalInput) finalInput = JSON.parse(fingerprint);
          if (!profileId) profileId = store.create({
            name: input.name.trim(), subjectType: options.personType, role: options.role,
            photo, photoPixels: options.photoPixels, eyeColor: options.eyeColor || '', hairColor: '',
            gender: input.storyMetadata?.gender || '', age: input.storyMetadata?.age ?? null,
            permission: options.permission
          }).id;
          flight = Promise.resolve().then(() => { guard(); return client.saveProfile(profileId, {
            productId: options.productId, requestKey: finalInput.requestKey
          }); }).then(saved => {
            guard();
            if (saved?.registered !== true || saved.profileId !== profileId) throw fail('CHARACTER_NOT_SAVED', 'Your character could not be confirmed. Try saving again.');
            photoDisplayUrl = store.get(profileId).thumbnailUrl;
            const currentDisplay = snapshot().portrait;
            const portrait = saved.portrait?.status === 'ready' ? saved.portrait :
              currentDisplay.url === sourceUrl ? { ...currentDisplay, url: photoDisplayUrl } : { ...currentDisplay };
            display = { ...portrait };
            result = { ...saved, profileId, portrait };
            revokeSource();
            return result;
          }).finally(() => { flight = null; });
          return flight;
        } catch (error) { return Promise.reject(error); }
      }
    });
  }
  const api = { createAdapter, durationMs: DURATION_MS };
  if (typeof module !== 'undefined' && module.exports) module.exports = Object.freeze(api);
  if (typeof window !== 'undefined') {
    let settings = {};
    try { settings = JSON.parse(document.querySelector('[data-personalization-v2-config]')?.textContent || '{}'); } catch (_) { /* Leave the existing flow active. */ }
    window.WonderbookCharacterSimulation = Object.freeze({ ...api, enabled: settings.characterPreviewSimulation === true });
  }
})();
